<template>
  <div class="user-password-container password-page nav-content-inset">
    <el-card class="user-password-card">
      <template #header>
        <h2 class="card-title">修改密码</h2>
      </template>

      <el-form
        ref="formRef"
        :model="form"
        :rules="rules"
        class="user-settings-form password-form"
        :label-position="compact ? 'top' : 'right'"
        :label-width="compact ? 'auto' : '120px'"
        @submit.prevent="handleSave"
      >
        <el-form-item label="当前密码" prop="oldPassword">
          <el-input
            v-model="form.oldPassword"
            type="password"
            placeholder="请输入当前密码"
            show-password
            autocomplete="current-password"
          />
        </el-form-item>

        <el-form-item label="新密码" prop="newPassword">
          <el-input
            v-model="form.newPassword"
            type="password"
            placeholder="请输入新密码"
            show-password
            autocomplete="new-password"
            maxlength="64"
          />
          <p class="field-hint">{{ passwordHint }}</p>
        </el-form-item>

        <el-form-item label="确认新密码" prop="confirmPassword">
          <el-input
            v-model="form.confirmPassword"
            type="password"
            placeholder="请再次输入新密码"
            show-password
            autocomplete="new-password"
            maxlength="64"
          />
        </el-form-item>

        <el-form-item class="password-form-actions">
          <el-button type="primary" native-type="submit" :loading="saving">
            保存
          </el-button>
          <el-button :disabled="saving" @click="resetForm">清空</el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import { ref, reactive, onMounted, onBeforeUnmount } from 'vue'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { validatePassword, passwordsMatch, PASSWORD_HINT } from '@/utils/password'
import { isCompactViewport } from '@/utils/browser'

const emptyForm = () => ({
  oldPassword: '',
  newPassword: '',
  confirmPassword: ''
})

export default {
  name: 'UserPassword',
  setup() {
    const form = reactive(emptyForm())
    const saving = ref(false)
    const formRef = ref(null)
    const passwordHint = PASSWORD_HINT
    const compact = ref(isCompactViewport())

    const onResize = () => {
      compact.value = isCompactViewport()
    }
    onMounted(() => window.addEventListener('resize', onResize))
    onBeforeUnmount(() => window.removeEventListener('resize', onResize))

    const validateNewPassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入新密码'))
        return
      }
      const result = validatePassword(value)
      if (!result.ok) {
        callback(new Error(result.message))
        return
      }
      if (form.confirmPassword) {
        formRef.value?.validateField('confirmPassword')
      }
      callback()
    }

    const validateConfirmPassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请再次输入新密码'))
        return
      }
      if (!passwordsMatch(form.newPassword, value)) {
        callback(new Error('两次输入的新密码不一致'))
        return
      }
      const result = validatePassword(value)
      if (!result.ok) {
        callback(new Error(result.message))
        return
      }
      callback()
    }

    const rules = {
      oldPassword: [{ required: true, message: '请输入当前密码', trigger: 'blur' }],
      newPassword: [{ required: true, validator: validateNewPassword, trigger: 'blur' }],
      confirmPassword: [{ required: true, validator: validateConfirmPassword, trigger: 'blur' }]
    }

    const resetForm = () => {
      Object.assign(form, emptyForm())
      formRef.value?.clearValidate()
    }

    const handleSave = async () => {
      if (!formRef.value) return
      try {
        await formRef.value.validate()
      } catch {
        ElMessage.error('请检查表单填写是否正确')
        return
      }

      if (form.oldPassword === form.newPassword) {
        ElMessage.warning('新密码不能与当前密码相同')
        return
      }
      const pwdCheck = validatePassword(form.newPassword)
      if (!pwdCheck.ok) {
        ElMessage.error(pwdCheck.message)
        return
      }
      if (!passwordsMatch(form.newPassword, form.confirmPassword)) {
        ElMessage.error('两次输入的新密码不一致')
        return
      }

      saving.value = true
      try {
        const token = localStorage.getItem('jwt_token')
        const { data } = await axios.post(
          '/api/v1/user/password',
          {
            oldPassword: form.oldPassword,
            newPassword: form.newPassword,
            confirmPassword: form.confirmPassword
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        )
        if (data.code === 200) {
          ElMessage.success(data.message || '密码修改成功')
          resetForm()
        } else {
          ElMessage.error(data.message || '密码修改失败')
        }
      } catch (err) {
        ElMessage.error(err.response?.data?.message || '密码修改失败')
      } finally {
        saving.value = false
      }
    }

    return {
      form,
      saving,
      formRef,
      rules,
      passwordHint,
      compact,
      resetForm,
      handleSave
    }
  }
}
</script>

<style scoped>
.user-password-container {
  max-width: 800px;
  margin: 20px auto;
  padding: 0 20px;
  box-sizing: border-box;
}

.user-password-card {
  margin-bottom: 20px;
}

.card-title {
  margin: 0;
  font-size: 1.5em;
  color: #303133;
}

.field-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}

:deep(.el-form-item__label) {
  font-weight: 500;
}

@media (max-width: 767px) {
  .user-password-container {
    margin: 0;
    padding: 0;
  }
}
</style>
